import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { CellProps } from '../../components/table2/ad_grid_columns'
import type { AgGridColumnDef } from '../../components/table2/AgGridTable'
import type { TableStore } from '../../components/table2/table_store'
import { Toasts } from '../../components/toaster/Toast'
import { Button } from '../../components/ui/button'
import { Popover } from '../../components/ui/popover'
import type { EntityId, SelectionSet } from '../../types/constants'
import { customFieldApiError, customFieldsApi, type CustomFieldValue } from './api'
import {
    CUSTOM_FIELD_DATA_TYPES,
    type CustomFieldDefinition,
    type CustomFieldEntityType,
} from './types'
import { useCustomFieldCatalog } from './useCustomFieldCatalog'

export const CUSTOM_FIELD_BULK_MUTATION_LIMIT = 10_000

export const selectedCustomFieldEntityIds = (
    selection: Pick<SelectionSet, 'all' | 'safeList'>,
): { ids: string[] | null; error: string | null } => {
    if (selection.all) {
        return {
            ids: null,
            error: 'Select explicit rows for this bounded write; “all results” cannot be enumerated safely.',
        }
    }
    const ids = Array.from(selection.safeList, (id: EntityId) => String(id))
    return ids.length > CUSTOM_FIELD_BULK_MUTATION_LIMIT
        ? {
              ids,
              error: `At most ${CUSTOM_FIELD_BULK_MUTATION_LIMIT.toLocaleString()} rows can be changed at once.`,
          }
        : { ids, error: null }
}

export type AdManagerCustomFieldRow = {
    custom_fields?: Record<string, CustomFieldValue>
} & Record<string, unknown>

const inputClass = 'min-w-0 rounded border border-gray-300 px-2 py-1 text-sm'

export const customFieldDisplayValue = (
    definition: Pick<CustomFieldDefinition, 'data_type'>,
    value: CustomFieldValue | undefined,
): string => {
    if (!value) return 'Not set'
    if (definition.data_type === CUSTOM_FIELD_DATA_TYPES.Boolean) {
        return value.value === true ? 'Yes' : 'No'
    }
    return String(value.value)
}

export const validateAdManagerCustomFieldValue = (
    definition: Pick<CustomFieldDefinition, 'data_type'>,
    value: string | boolean | null,
): string | null => {
    if (value === null) return null
    if (definition.data_type === CUSTOM_FIELD_DATA_TYPES.Boolean) {
        return typeof value === 'boolean' ? null : 'Choose Yes, No, or Not set'
    }
    if (typeof value !== 'string' || value.length === 0) return 'Empty strings are not allowed'
    if (
        definition.data_type === CUSTOM_FIELD_DATA_TYPES.Number &&
        !/^-?(?:0|[1-9]\d{0,19})(?:\.\d{1,10})?$/.test(value)
    ) {
        return 'Enter a valid decimal number'
    }
    if (Array.from(value).length > 250) return 'Values must be at most 250 characters'
    return null
}

export const mutateSelectedCustomFieldValues = async (input: {
    profileId: string
    entityType: CustomFieldEntityType
    definition: CustomFieldDefinition
    entityIds: string[]
    operation: 'set' | 'clear'
    value: string | boolean | null
}) => {
    // Fetch immediately before writing so every row participates in the API's
    // all-or-nothing optimistic concurrency check.
    const snapshot = await customFieldsApi.snapshotValues(
        input.profileId,
        input.entityType,
        input.entityIds,
    )
    return customFieldsApi.mutateValues(
        input.profileId,
        input.entityType,
        input.entityIds.map((entityId) => ({
            field_definition_id: input.definition.definition_id,
            entity_id: entityId,
            operation: input.operation,
            ...(input.operation === 'set' ? { value: input.value as string | boolean } : {}),
            expected_version: snapshot[entityId]?.[input.definition.key]?.version ?? null,
        })),
        'ad-manager-bulk',
    )
}

const ValueInput = ({
    definition,
    value,
    setValue,
}: {
    definition: CustomFieldDefinition
    value: string | boolean | null
    setValue: (value: string | boolean | null) => void
}) =>
    definition.data_type === CUSTOM_FIELD_DATA_TYPES.Boolean ? (
        <select
            aria-label={`${definition.name} value`}
            className={inputClass}
            value={value === null ? '' : value ? 'true' : 'false'}
            onChange={(event) =>
                setValue(event.target.value === '' ? null : event.target.value === 'true')
            }
        >
            <option value="">Not set</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
        </select>
    ) : (
        <input
            aria-label={`${definition.name} value`}
            className={inputClass}
            value={typeof value === 'string' ? value : ''}
            maxLength={definition.data_type === CUSTOM_FIELD_DATA_TYPES.String ? 250 : undefined}
            placeholder={definition.data_type === CUSTOM_FIELD_DATA_TYPES.Number ? '0.00' : 'Value'}
            onChange={(event) => setValue(event.target.value)}
        />
    )

export const CustomFieldCell = <T,>({
    data,
    definition,
    profileId,
    entityType,
    entityIdKey,
    reload,
}: CellProps<T> & {
    definition: CustomFieldDefinition
    profileId: string
    entityType: CustomFieldEntityType
    entityIdKey: keyof T
    reload: () => void
}) => {
    const row = data as T & AdManagerCustomFieldRow
    const current = row.custom_fields?.[definition.key]
    const [editing, setEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [value, setValue] = useState<string | boolean | null>(current?.value ?? null)
    const [expectedVersion, setExpectedVersion] = useState<string | null>(null)
    const entityIdValue = data[entityIdKey]
    const entityId =
        entityIdValue === null || entityIdValue === undefined ? null : String(entityIdValue)
    const editScope = `${profileId}:${entityType}:${entityId ?? 'missing'}:${definition.definition_id}`
    const activeEditScope = useRef(editScope)
    const latestSaveRequest = useRef(0)
    activeEditScope.current = editScope

    useEffect(() => {
        latestSaveRequest.current++
        setSaving(false)
        setEditing(false)
        setExpectedVersion(null)
        return () => {
            latestSaveRequest.current++
        }
    }, [editScope])

    const save = async (operation: 'set' | 'clear') => {
        const error =
            operation === 'set' ? validateAdManagerCustomFieldValue(definition, value) : null
        if (error) return Toasts.error(error)
        if (entityId === null) return Toasts.error('Entity ID is missing')
        const requestedScope = editScope
        const request = ++latestSaveRequest.current
        const requestIsCurrent = () =>
            request === latestSaveRequest.current && activeEditScope.current === requestedScope
        setSaving(true)
        try {
            await customFieldsApi.mutateValues(
                profileId,
                entityType,
                [
                    {
                        field_definition_id: definition.definition_id,
                        entity_id: entityId,
                        operation,
                        ...(operation === 'set' ? { value: value as string | boolean } : {}),
                        expected_version: expectedVersion,
                    },
                ],
                'ad-manager-inline',
            )
            if (requestIsCurrent()) {
                setEditing(false)
                reload()
            }
        } catch (requestError) {
            if (requestIsCurrent()) Toasts.error(customFieldApiError(requestError))
        } finally {
            if (requestIsCurrent()) setSaving(false)
        }
    }

    if (!editing) {
        return (
            <button
                type="button"
                className="w-full truncate text-left"
                title="Click to edit"
                onClick={() => {
                    setValue(current?.value ?? null)
                    setExpectedVersion(current?.version ?? null)
                    setEditing(true)
                }}
            >
                {customFieldDisplayValue(definition, current)}
            </button>
        )
    }

    return (
        <div className="flex h-full items-center gap-1">
            <ValueInput definition={definition} value={value} setValue={setValue} />
            <Button size="sm" disabled={saving || value === null} onClick={() => save('set')}>
                Set
            </Button>
            <Button
                size="sm"
                variant="outline"
                disabled={saving || expectedVersion === null}
                onClick={() => save('clear')}
            >
                Clear
            </Button>
        </div>
    )
}

export const buildAdManagerCustomFieldColumns = <T,>(
    definitions: readonly CustomFieldDefinition[],
    options: {
        profileId: string
        entityType: CustomFieldEntityType
        entityIdKey: keyof T
        reload: () => void
    },
): AgGridColumnDef<T>[] =>
    definitions.map((definition) => ({
        field: `custom.${definition.key}` as keyof T,
        headerName: definition.name,
        hide: true,
        width: 220,
        tooltip: definition.description ?? `Custom ${definition.type} field`,
        cellRenderer: ((props: CellProps<T>) => (
            <CustomFieldCell
                {...props}
                definition={definition}
                profileId={options.profileId}
                entityType={options.entityType}
                entityIdKey={options.entityIdKey}
                reload={options.reload}
            />
        )) as unknown as React.ReactNode,
    }))

export const BulkSetCustomField = <T,>({
    definitions,
    profileId,
    entityType,
    store,
    reload,
}: {
    definitions: readonly CustomFieldDefinition[]
    profileId: string
    entityType: CustomFieldEntityType
    store: TableStore<T>
    reload: () => void
}) => {
    const [definitionId, setDefinitionId] = useState(definitions[0]?.definition_id ?? '')
    const [value, setValue] = useState<string | boolean | null>(null)
    const [saving, setSaving] = useState(false)
    const definition = definitions.find((field) => field.definition_id === definitionId)
    const firstDefinitionId = definitions[0]?.definition_id ?? ''
    const definitionAvailable = definitions.some((field) => field.definition_id === definitionId)
    const { ids: selectedIds, error: selectionError } = selectedCustomFieldEntityIds(
        store.selection,
    )
    const mutationScope = `${profileId}:${entityType}:${definitionId || 'missing'}`
    const activeMutationScope = useRef(mutationScope)
    const latestMutationRequest = useRef(0)
    activeMutationScope.current = mutationScope

    useEffect(() => {
        setDefinitionId(firstDefinitionId)
        setValue(null)
    }, [entityType, profileId])

    useEffect(() => {
        if (!definitionAvailable) {
            setDefinitionId(firstDefinitionId)
            setValue(null)
        }
    }, [definitionAvailable, firstDefinitionId])

    useEffect(() => {
        latestMutationRequest.current++
        setSaving(false)
        return () => {
            latestMutationRequest.current++
        }
    }, [mutationScope])

    const mutate = async (operation: 'set' | 'clear') => {
        if (!definition || !selectedIds?.length) return
        const error =
            operation === 'set' ? validateAdManagerCustomFieldValue(definition, value) : null
        if (error) return Toasts.error(error)
        const requestedScope = mutationScope
        const request = ++latestMutationRequest.current
        const requestIsCurrent = () =>
            request === latestMutationRequest.current &&
            activeMutationScope.current === requestedScope
        setSaving(true)
        try {
            await mutateSelectedCustomFieldValues({
                profileId,
                entityType,
                definition,
                entityIds: selectedIds,
                operation,
                value,
            })
            if (requestIsCurrent()) {
                Toasts.success(`Updated ${selectedIds.length.toLocaleString()} selected rows`)
                store.deselectAll()
                reload()
            }
        } catch (requestError) {
            if (requestIsCurrent()) Toasts.error(customFieldApiError(requestError))
        } finally {
            if (requestIsCurrent()) setSaving(false)
        }
    }

    if (definitions.length === 0) return null
    return (
        <Popover
            openOnTargetFocus={false}
            content={
                <div className="w-80 space-y-3 p-3">
                    <div className="font-medium">Set custom field on selection</div>
                    <select
                        aria-label="Custom field"
                        className={`${inputClass} w-full`}
                        value={definitionId}
                        onChange={(event) => {
                            setDefinitionId(event.target.value)
                            setValue(null)
                        }}
                    >
                        {definitions.map((field) => (
                            <option key={field.definition_id} value={field.definition_id}>
                                {field.name} ({field.type})
                            </option>
                        ))}
                    </select>
                    {definition && (
                        <ValueInput definition={definition} value={value} setValue={setValue} />
                    )}
                    {selectionError && <div className="text-xs text-red-700">{selectionError}</div>}
                    <div className="flex gap-2">
                        <Button
                            disabled={
                                saving || !!selectionError || !selectedIds?.length || value === null
                            }
                            onClick={() => mutate('set')}
                        >
                            Set
                        </Button>
                        <Button
                            variant="outline"
                            disabled={saving || !!selectionError || !selectedIds?.length}
                            onClick={() => mutate('clear')}
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            }
        >
            <Button
                className="mr-2"
                variant="outline"
                disabled={store.selection.selectedCount === 0}
            >
                Custom fields
            </Button>
        </Popover>
    )
}

export const useAdManagerCustomFields = <T,>({
    profileId,
    entityType,
    entityIdKey,
    store,
    reload,
}: {
    profileId: string | null
    entityType: CustomFieldEntityType
    entityIdKey: keyof T
    store: TableStore<T>
    reload: () => void
}) => {
    const { catalog } = useCustomFieldCatalog(profileId, entityType)
    const definitions = catalog?.definitions ?? []
    const columns = useMemo(
        () =>
            profileId
                ? buildAdManagerCustomFieldColumns<T>(definitions, {
                      profileId,
                      entityType,
                      entityIdKey,
                      reload,
                  })
                : [],
        [definitions, entityIdKey, entityType, profileId, reload],
    )
    const bulkAction = profileId ? (
        <BulkSetCustomField
            definitions={definitions}
            profileId={profileId}
            entityType={entityType}
            store={store}
            reload={reload}
        />
    ) : null
    return { columns, bulkAction }
}
